import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2, ShieldCheck, Gauge, AlertTriangle } from "lucide-react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import SiteShell from "../site/SiteShell";
import PageHero from "../site/PageHero";
import { Reveal } from "../site/motion";
import { TRADES, TURNOVER_BANDS, PACKAGE_VALUES, CLIENT_TYPE_OPTIONS } from "../site/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INIT = {
  company_name: "", company_number: "", years_trading: "", turnover_band: "", employees: "", subcontractors_used: "",
  contact_name: "", email: "", telephone: "", website: "",
  trades: [], other_trade: "",
  typical_package_value: "", largest_project: "", coverage: "", current_capacity: "", simultaneous_projects: "", delivery_model: "", typical_client_type: "",
  insurances: {}, accreditations: {},
  hs_policy: null, produces_rams: null, method_statements: null, training_records: null, recent_references: null, insurance_certs_available: null,
  payment_terms_restrict: null, can_fund_30_60: null, invoice_finance_helpful: null, finance_intro_requested: null,
};

const INSURANCES = [
  { key: "public_liability", label: "Public Liability Insurance" },
  { key: "employers_liability", label: "Employers' Liability Insurance" },
  { key: "professional_indemnity", label: "Professional Indemnity (where applicable)" },
];

const ACCREDITATIONS = [
  { key: "ssip", label: "SSIP" }, { key: "chas", label: "CHAS" },
  { key: "smas", label: "SMAS" }, { key: "safecontractor", label: "SafeContractor" },
  { key: "constructionline", label: "Constructionline" }, { key: "iso_9001", label: "ISO 9001" },
  { key: "iso_14001", label: "ISO 14001" }, { key: "iso_45001", label: "ISO 45001" },
  { key: "niceic", label: "NICEIC" }, { key: "gas_safe", label: "Gas Safe" },
  { key: "other", label: "Other trade-specific accreditation" },
];

const YN_FIELDS = [
  { key: "hs_policy", label: "Do you have a written Health & Safety policy?" },
  { key: "produces_rams", label: "Do you regularly produce RAMS?" },
  { key: "method_statements", label: "Can you provide project-specific method statements?" },
  { key: "training_records", label: "Do you maintain training & competency records?" },
  { key: "recent_references", label: "Can you provide recent client references?" },
  { key: "insurance_certs_available", label: "Do you have current insurance certificates available?" },
];

const FIN_FIELDS = [
  { key: "payment_terms_restrict", label: "Do extended payment terms restrict the contracts you can accept?" },
  { key: "can_fund_30_60", label: "Could you fund wages/materials for 30–60 days before payment?" },
  { key: "invoice_finance_helpful", label: "Would invoice finance potentially help you take on larger contracts?" },
  { key: "finance_intro_requested", label: "Would you like Verified Workforce to introduce you to its finance partner?" },
];

const STEPS = ["Company", "Trade", "Commercial", "Compliance", "Finance", "Result"];

export default function Readiness() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleTrade = (t) => set({ trades: form.trades.includes(t) ? form.trades.filter((x) => x !== t) : [...form.trades, t] });
  const setIns = (k, v) => set({ insurances: { ...form.insurances, [k]: v } });
  const setAcc = (k, v) => set({ accreditations: { ...form.accreditations, [k]: v } });

  const canAdvance = () => {
    if (step === 0) return form.company_name && form.contact_name && form.email;
    return true;
  };

  const next = () => {
    if (!canAdvance()) {
      toast.error("Please complete the required fields on this step.");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/readiness`, form);
      setResult(data);
      setStep(STEPS.length - 1);
      toast.success("Assessment complete.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteShell>
      <PageHero
        overline="Contractor Readiness Assessment"
        title="Check your contract readiness in a few minutes."
        kicker="A simple, structured review of where your business sits against the expectations of larger construction clients. This is not an external accreditation — it is a Verified Workforce internal readiness indicator."
        image="https://images.unsplash.com/photo-1529220502050-f15e570c634e?auto=format&fit=crop&w=1800&q=70"
      >
        <div className="flex flex-wrap gap-3">
          <a href="#assessment" className="vw-btn vw-btn--primary" data-testid="readiness-start-cta">
            Start Assessment
            <ArrowRight size={16} />
          </a>
          <Link to="/compliance" className="vw-btn vw-btn--ghost">
            Learn About Compliance Support
          </Link>
        </div>
      </PageHero>

      <section id="assessment" className="vw-section bg-[var(--vw-paper)]" data-testid="readiness-section">
        <div className="vw-container">
          {/* Step indicator */}
          <div className="mb-10 grid grid-cols-6 gap-2 max-w-3xl mx-auto">
            {STEPS.map((s, i) => (
              <div key={s} className="flex flex-col gap-2">
                <div className={`h-1 ${i <= step ? "bg-[var(--vw-blue)]" : "bg-[var(--vw-line)]"}`} />
                <div className={`text-[10px] font-mono tracking-[0.18em] uppercase ${i === step ? "text-[var(--vw-blue)]" : "text-[var(--vw-muted)]"}`}>
                  0{i + 1} {s}
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto bg-white p-8 lg:p-12 border border-[var(--vw-line)]">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`readiness-step-${step}`}
              >
                {step === 0 && <StepCompany form={form} set={set} />}
                {step === 1 && <StepTrades form={form} set={set} toggleTrade={toggleTrade} />}
                {step === 2 && <StepCommercial form={form} set={set} />}
                {step === 3 && <StepCompliance form={form} set={set} setIns={setIns} setAcc={setAcc} />}
                {step === 4 && <StepFinance form={form} set={set} />}
                {step === 5 && <StepResult result={result} />}
              </motion.div>
            </AnimatePresence>

            {step < STEPS.length - 1 && (
              <div className="mt-10 pt-8 border-t border-[var(--vw-line)] flex items-center justify-between gap-3">
                <button
                  onClick={back}
                  disabled={step === 0}
                  className="vw-btn vw-btn--outline-dark vw-btn--sm disabled:opacity-50"
                  data-testid="readiness-back-btn"
                >
                  <ArrowLeft size={14}/> Back
                </button>
                {step < 4 ? (
                  <button onClick={next} className="vw-btn vw-btn--primary" data-testid="readiness-next-btn">
                    Continue <ArrowRight size={14}/>
                  </button>
                ) : (
                  <button onClick={submit} disabled={loading} className="vw-btn vw-btn--primary" data-testid="readiness-submit-btn">
                    {loading ? <><Loader2 size={14} className="animate-spin"/> Scoring…</> : <>Get My Result <ArrowRight size={14}/></>}
                  </button>
                )}
              </div>
            )}
          </div>

          <p className="mt-6 max-w-3xl mx-auto text-center text-xs text-[var(--vw-muted)]">
            This assessment is a Verified Workforce internal readiness indicator. It is not
            an external accreditation. Preferred Delivery Partner status can only be
            awarded manually by Verified Workforce following review and demonstrable
            delivery performance.
          </p>
        </div>
      </section>
    </SiteShell>
  );
}

function H({ children }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="vw-rule-gold"/>
      <h2 className="font-display text-2xl lg:text-3xl font-semibold text-[var(--vw-navy)]">{children}</h2>
    </div>
  );
}

function Input({ label, name, value, onChange, type = "text", required, placeholder, testid }) {
  const id = `field-${testid || name}`;
  return (
    <div className="vw-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} name={name} value={value || ""} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}/>
    </div>
  );
}

function Select({ label, name, value, onChange, options, testid }) {
  const id = `field-${testid || name}`;
  return (
    <div className="vw-field">
      <label htmlFor={id}>{label}</label>
      <select id={id} name={name} value={value || ""} onChange={onChange} data-testid={testid}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-[var(--vw-line)]">
      <span className="text-sm text-[var(--vw-charcoal)]">{label}</span>
      <div className="flex gap-2 flex-shrink-0">
        {[["yes", true], ["no", false]].map(([k, v]) => (
          <button
            key={k}
            type="button"
            onClick={() => onChange(v)}
            className={`px-3 py-1.5 text-xs font-mono tracking-[0.14em] uppercase border transition-colors ${value === v ? "bg-[var(--vw-navy)] text-white border-[var(--vw-navy)]" : "bg-white text-[var(--vw-navy)] border-[var(--vw-line)] hover:border-[var(--vw-blue)]"}`}
            data-testid={`${testid}-${k}`}
          >
            {k}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepCompany({ form, set }) {
  const oc = (e) => set({ [e.target.name]: e.target.value });
  return (
    <div>
      <H>Company Information</H>
      <div className="grid sm:grid-cols-2 gap-5">
        <Input label="Company Name *" name="company_name" value={form.company_name} onChange={oc} required testid="ra-company"/>
        <Input label="Companies House Number" name="company_number" value={form.company_number} onChange={oc} testid="ra-company-no"/>
        <Input label="Years Trading" name="years_trading" value={form.years_trading} onChange={oc} testid="ra-years"/>
        <Select label="Annual Turnover" name="turnover_band" value={form.turnover_band} onChange={oc} options={TURNOVER_BANDS} testid="ra-turnover"/>
        <Input label="Employees" name="employees" value={form.employees} onChange={oc} testid="ra-employees"/>
        <Input label="Subcontractors Used" name="subcontractors_used" value={form.subcontractors_used} onChange={oc} testid="ra-subs"/>
        <Input label="Main Contact *" name="contact_name" value={form.contact_name} onChange={oc} required testid="ra-contact"/>
        <Input label="Email *" type="email" name="email" value={form.email} onChange={oc} required testid="ra-email"/>
        <Input label="Telephone" name="telephone" value={form.telephone} onChange={oc} testid="ra-phone"/>
        <Input label="Website" name="website" value={form.website} onChange={oc} testid="ra-website"/>
      </div>
    </div>
  );
}

function StepTrades({ form, set, toggleTrade }) {
  return (
    <div>
      <H>Trades &amp; Specialism</H>
      <p className="mb-6 text-sm text-[var(--vw-muted)]">Select every area your business delivers in.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {TRADES.map((t) => {
          const active = form.trades.includes(t);
          return (
            <label key={t} className={`vw-check ${active ? "vw-check--active" : ""}`} data-testid={`ra-trade-${t.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
              <input type="checkbox" checked={active} onChange={() => toggleTrade(t)}/>
              <span>{t}</span>
            </label>
          );
        })}
      </div>
      {form.trades.includes("Other") && (
        <div className="mt-5">
          <Input label="Please specify other trade" name="other_trade" value={form.other_trade} onChange={(e)=>set({other_trade:e.target.value})} testid="ra-other-trade"/>
        </div>
      )}
    </div>
  );
}

function StepCommercial({ form, set }) {
  const oc = (e) => set({ [e.target.name]: e.target.value });
  return (
    <div>
      <H>Commercial Capability</H>
      <div className="grid sm:grid-cols-2 gap-5">
        <Select label="Typical Project / Package Value" name="typical_package_value" value={form.typical_package_value} onChange={oc} options={PACKAGE_VALUES} testid="ra-value"/>
        <Input label="Largest Project Completed" name="largest_project" value={form.largest_project} onChange={oc} testid="ra-largest"/>
        <Input label="Geographic Coverage" name="coverage" value={form.coverage} onChange={oc} placeholder="e.g. London, South East" testid="ra-coverage"/>
        <Input label="Current Capacity" name="current_capacity" value={form.current_capacity} onChange={oc} placeholder="e.g. 60% utilised" testid="ra-capacity"/>
        <Input label="Simultaneous Projects" name="simultaneous_projects" value={form.simultaneous_projects} onChange={oc} testid="ra-simul"/>
        <Select label="Delivery Model" name="delivery_model" value={form.delivery_model} onChange={oc} options={["Labour-only","Supply-and-fit","Both"]} testid="ra-model"/>
        <Select label="Typical Client Type" name="typical_client_type" value={form.typical_client_type} onChange={oc} options={CLIENT_TYPE_OPTIONS} testid="ra-client-type"/>
      </div>
    </div>
  );
}

function StepCompliance({ form, setIns, setAcc, set }) {
  return (
    <div>
      <H>Compliance</H>
      <h3 className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vw-blue)] mb-3">Insurances</h3>
      <div className="grid sm:grid-cols-3 gap-2 mb-8">
        {INSURANCES.map((i) => (
          <label key={i.key} className={`vw-check ${form.insurances[i.key] ? "vw-check--active" : ""}`} data-testid={`ra-ins-${i.key}`}>
            <input type="checkbox" checked={!!form.insurances[i.key]} onChange={(e)=>setIns(i.key, e.target.checked)}/>
            <span>{i.label}</span>
          </label>
        ))}
      </div>
      <h3 className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vw-blue)] mb-3">Accreditations</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-8">
        {ACCREDITATIONS.map((a) => (
          <label key={a.key} className={`vw-check ${form.accreditations[a.key] ? "vw-check--active" : ""}`} data-testid={`ra-acc-${a.key}`}>
            <input type="checkbox" checked={!!form.accreditations[a.key]} onChange={(e)=>setAcc(a.key, e.target.checked)}/>
            <span>{a.label}</span>
          </label>
        ))}
      </div>
      <h3 className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vw-blue)] mb-3">Systems &amp; Documentation</h3>
      <div className="divide-y divide-[var(--vw-line)]">
        {YN_FIELDS.map((f) => (
          <YesNo key={f.key} label={f.label} value={form[f.key]} onChange={(v)=>set({[f.key]:v})} testid={`ra-${f.key.replace(/_/g,"-")}`}/>
        ))}
      </div>
    </div>
  );
}

function StepFinance({ form, set }) {
  return (
    <div>
      <H>Financial Readiness</H>
      <p className="mb-6 text-sm text-[var(--vw-muted)]">
        Cash flow is one of the biggest blockers to contractors taking on larger
        contracts. Your answers help us understand whether an introduction to our
        finance partner could help you.
      </p>
      <div className="divide-y divide-[var(--vw-line)]">
        {FIN_FIELDS.map((f) => (
          <YesNo key={f.key} label={f.label} value={form[f.key]} onChange={(v)=>set({[f.key]:v})} testid={`ra-${f.key.replace(/_/g,"-")}`}/>
        ))}
      </div>
    </div>
  );
}

function StepResult({ result }) {
  if (!result) {
    return (
      <div className="text-center py-10 text-[var(--vw-muted)]" data-testid="readiness-no-result">
        No result yet. Please complete the previous steps.
      </div>
    );
  }
  const map = {
    developing: {
      icon: <AlertTriangle size={28} />,
      label: "Developing",
      tone: "amber",
      strap: "Some areas need strengthening before larger opportunities.",
    },
    contract_ready: {
      icon: <ShieldCheck size={28} />,
      label: "Contract Ready",
      tone: "blue",
      strap: "Your business appears to have many of the foundations required for larger opportunities.",
    },
    review_required: {
      icon: <Gauge size={28} />,
      label: "Verified Workforce Review",
      tone: "gold",
      strap: "Submit your supporting documents for assessment by our team.",
    },
  };
  const d = map[result.tier] || map.developing;
  const toneBg = d.tone === "amber" ? "bg-amber-50 border-amber-300 text-amber-900" : d.tone === "blue" ? "bg-blue-50 border-[var(--vw-blue)] text-[var(--vw-blue-dark)]" : "bg-[var(--vw-cream)] border-[var(--vw-gold)] text-[#7A5F10]";

  return (
    <div data-testid="readiness-result">
      <div className="vw-overline mb-4">Your Readiness Indicator</div>
      <h2 className="font-display text-3xl lg:text-5xl font-semibold text-[var(--vw-navy)] tracking-tight leading-[1.05]">
        {d.label}
      </h2>
      <p className="mt-5 text-[var(--vw-muted)] max-w-2xl leading-relaxed">
        {result.tier_message || d.strap}
      </p>

      <div className={`mt-10 border p-7 ${toneBg}`}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 flex items-center justify-center bg-white/70">
            {d.icon}
          </div>
          <div>
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase opacity-70">Score</div>
            <div className="font-display text-4xl font-semibold">{result.score}/100</div>
          </div>
        </div>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        <Link to="/compliance" className="vw-btn vw-btn--primary" data-testid="readiness-cta-compliance">
          See How We Support Compliance <ArrowRight size={14}/>
        </Link>
        <Link to="/delivery-partners#apply" className="vw-btn vw-btn--outline-dark" data-testid="readiness-cta-apply">
          Apply to the Delivery Network
        </Link>
        {result.tier === "review_required" && (
          <Link to="/contact" className="vw-btn vw-btn--gold sm:col-span-2" data-testid="readiness-cta-review">
            Submit Documents for Verified Workforce Review
          </Link>
        )}
      </div>

      <div className="mt-8 flex items-start gap-2 text-xs text-[var(--vw-muted)]">
        <CheckCircle2 size={14} className="mt-0.5 text-[var(--vw-blue)]"/>
        <span>This is a Verified Workforce internal readiness indicator — not an external accreditation. Preferred Delivery Partner status is only awarded manually following review and demonstrable delivery performance.</span>
      </div>
    </div>
  );
}
